/**
 * Country-aware phone-number normalization for Femcare web auth.
 *
 * Why this exists:
 *   The web signup / login screens ask the user to pick a country *code* (+91,
 *   +1, +44, …) separately from the *national* number they type. A naive
 *   concatenation like  `+91` + `09873162626`  produces the illegal E.164
 *   +91098… which either fails Firebase Phone Auth outright OR is accepted
 *   as a *different* canonical identity than the mobile app's E.164
 *   +919873162626, silently creating a duplicate account.
 *
 *   We use Google's libphonenumber-js so trunk-prefix handling is done per
 *   country (India strips leading `0`; France strips leading `0`; the US
 *   does NOT have a national trunk prefix), instead of a naive global rule.
 *
 * Public API (both take the SAME `dialCode` the country dropdown emits):
 *   normalizeE164(dialCode, rawNational)  → "+919873162626"  (or null on error)
 *   isValidPhone (dialCode, rawNational)  → boolean
 *
 * The mapping between the dial-code shown in the UI (+91) and the ISO code
 * libphonenumber wants ("IN") lives here so the UI never has to know about
 * libphonenumber internals. Keep in sync with the COUNTRIES list in
 * AuthPages.jsx — if a country is added there, add its ISO here.
 */
import { parsePhoneNumberFromString } from "libphonenumber-js";

// Dial code (as shown in the UI select) → ISO-3166 alpha-2 country code
// (as libphonenumber-js expects for `defaultCountry`).
export const DIAL_TO_ISO = {
    "+91":  "IN",
    "+1":   "US",   // shared with CA; libphonenumber treats US as default for +1
    "+44":  "GB",
    "+61":  "AU",
    "+65":  "SG",
    "+971": "AE",
    "+234": "NG",
    "+27":  "ZA",
    "+49":  "DE",
    "+33":  "FR",
};

/**
 * Return the canonical E.164 form (e.g. "+919873162626") for the given
 * user-entered national number, or `null` if the number cannot be parsed
 * for that country.
 *
 * Idempotent: if the user already typed the number in full E.164 form
 * (e.g. pasted "+919873162626") the same E.164 is returned regardless of
 * which country the dropdown is on.
 */
export function normalizeE164(dialCode, rawNational) {
    if (!rawNational) return null;
    const iso = DIAL_TO_ISO[dialCode];
    const raw = String(rawNational).trim();
    if (!iso && !raw.startsWith("+")) return null;

    // If the user pasted a full E.164 ("+91..."), libphonenumber ignores the
    // defaultCountry hint and uses the +XX prefix directly.
    const parsed = parsePhoneNumberFromString(raw, iso);
    if (parsed && parsed.isValid()) return parsed.number;

    // Fallback: user typed only national digits with no `+` prefix but the
    // digits already start with the dial code (e.g. `919873162626` in IN).
    // Guard against `+91 + 919…` → `+91919…` duplication by re-parsing as
    // if the leading match were the country code.
    const digits = raw.replace(/[^\d]/g, "");
    if (digits && dialCode && digits.startsWith(dialCode.replace("+", ""))) {
        const p2 = parsePhoneNumberFromString("+" + digits);
        if (p2 && p2.isValid()) return p2.number;
    }
    return null;
}

/**
 * Convenience: `true` iff normalizeE164 would return a value.
 * Cheap enough to call on every input change for live validation.
 */
export function isValidPhone(dialCode, rawNational) {
    return normalizeE164(dialCode, rawNational) !== null;
}
